import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";

export interface ClientDriver {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  zip: string;
  city: string;
  rfid: string;
}

interface RemoteDriver {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  zip?: string;
  city?: string;
  rfid?: string | null;
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

function mapDriver(driver: RemoteDriver): ClientDriver {
  return {
    id: driver.id,
    firstName: driver.first_name?.trim() ?? "",
    lastName: driver.last_name?.trim() ?? "",
    email: driver.email?.trim() ?? "",
    phone: driver.phone?.trim() ?? "",
    address: driver.address?.trim() ?? "",
    zip: driver.zip?.trim() ?? "",
    city: driver.city?.trim() ?? "",
    rfid: driver.rfid?.trim() ?? "",
  };
}

export async function listDrivers(): Promise<ClientDriver[]> {
  const response = await apiRequest<ListResponse<RemoteDriver>>("/api/drivers", {
    method: "GET",
    cache: "no-store",
  });
  return response.data.map(mapDriver);
}

/** What the activity log calls this driver. Empty when neither name was supplied. */
function driverLabel(input: Partial<Omit<ClientDriver, "id">>): string {
  return `${input.firstName ?? ""} ${input.lastName ?? ""}`.trim();
}

export async function createDriver(
  input: Omit<ClientDriver, "id">,
): Promise<void> {
  const response = await apiRequest<{ success: true; id: string }>("/api/drivers", {
    method: "POST",
    body: JSON.stringify({
      first_name: input.firstName,
      last_name: input.lastName,
      email: input.email,
      phone: input.phone,
      address: input.address,
      zip: input.zip,
      city: input.city,
      rfid: input.rfid,
    }),
  });
  logCrud("create", "driver", driverLabel(input), response.id);
}

export async function updateDriver(
  id: string,
  input: Partial<Omit<ClientDriver, "id">>,
): Promise<void> {
  await apiRequest<{ success: true }>(`/api/drivers/${id}`, {
    method: "PUT",
    body: JSON.stringify({
      ...(input.firstName !== undefined ? { first_name: input.firstName } : {}),
      ...(input.lastName !== undefined ? { last_name: input.lastName } : {}),
      ...(input.email !== undefined ? { email: input.email } : {}),
      ...(input.phone !== undefined ? { phone: input.phone } : {}),
      ...(input.address !== undefined ? { address: input.address } : {}),
      ...(input.zip !== undefined ? { zip: input.zip } : {}),
      ...(input.city !== undefined ? { city: input.city } : {}),
      ...(input.rfid !== undefined ? { rfid: input.rfid } : {}),
    }),
  });
  logCrud("update", "driver", driverLabel(input), id);
}

export async function deleteDriver(id: string): Promise<void> {
  await apiRequest<{ success: true }>(`/api/drivers/${id}`, {
    method: "DELETE",
  });
  logCrud("delete", "driver", "", id);
}
